import type { FxCurrency } from "@trader/shared/FxApi"
import type { FormInput } from "../../ui/InputField.tsx"
import { currencyOptions } from "./accountCurrency.ts"

// TradingView's strategy Properties tab, as form fields over the backtester's
// StrategyProperties (camelCase keys). The baseline values come from each run
// (the strategy's own strategy() declaration over the engine's defaults).

const group = (name: string) => ({ group: name })

export const PROPERTY_FIELDS: ReadonlyArray<FormInput> = [
  { id: "initialCapital", type: "float", title: "Initial capital", min: 0, ...group("Capital") },
  {
    id: "defaultQtyType",
    type: "string",
    title: "Order size type",
    options: ["fixed", "cash", "percent_of_equity"],
    tooltip: "fixed: contracts · cash: money per order · percent_of_equity: % of equity per order",
    ...group("Order size")
  },
  { id: "defaultQtyValue", type: "float", title: "Order size", min: 0, ...group("Order size") },
  {
    id: "pyramiding",
    type: "int",
    title: "Pyramiding (entries in one direction)",
    min: 0,
    max: 100,
    tooltip: "As in Pine: 0 and 1 both allow one entry per direction",
    ...group("Order size")
  },
  { id: "marginLong", type: "float", title: "Margin for long positions, %", min: 0, max: 100, ...group("Margin") },
  { id: "marginShort", type: "float", title: "Margin for short positions, %", min: 0, max: 100, ...group("Margin") },
  {
    id: "commissionType",
    type: "string",
    title: "Commission type",
    options: ["percent", "cash_per_contract", "cash_per_order"],
    ...group("Costs")
  },
  { id: "commissionValue", type: "float", title: "Commission", min: 0, ...group("Costs") },
  { id: "slippage", type: "int", title: "Slippage, ticks", min: 0, ...group("Costs") },
  { id: "closeEntriesRule", type: "string", title: "Close entries rule", options: ["FIFO", "ANY"], ...group("Execution") },
  { id: "processOrdersOnClose", type: "bool", title: "Process orders on bar close", ...group("Execution") }
]

/**
 * Properties › Capital › Account currency: the symbol's currency by default,
 * any currency the server has rates for otherwise (amounts are converted per
 * bar, never relabelled). Only a choice other than the symbol's is stored.
 */
export const accountCurrencyField = (
  symbolCurrency: string,
  currencies: ReadonlyArray<FxCurrency> | null,
  selected: string
): FormInput => {
  const options = currencyOptions(symbolCurrency, currencies, selected)
  return {
    id: "currency",
    type: "string",
    title: "Account currency",
    options: options.map((option) => option.code),
    optionLabels: Object.fromEntries(options.map((option) => [option.code, option.label])),
    tooltip: "Profit, equity and commissions in this currency, converted at each bar's previous-day ECB rate. " +
      "Initial capital and cash order sizes are in it too; prices stay in the symbol's currency.",
    group: "Capital"
  }
}

/** The Properties form: PROPERTY_FIELDS with the account currency after the initial capital. */
export const propertyFieldsWith = (currency: FormInput): ReadonlyArray<FormInput> => {
  const at = PROPERTY_FIELDS.findIndex((field) => field.id === "initialCapital") + 1
  return [...PROPERTY_FIELDS.slice(0, at), currency, ...PROPERTY_FIELDS.slice(at)]
}
